import { useAuth0 } from "@auth0/auth0-react";
import { isTauri } from "@tauri-apps/api/core";
import { useCallback, useEffect, useState } from "react";
import { exchangeSessionFromAuth0, fetchMe, logoutApiSession, type MeResponse } from "../lib/api/client";
import { getApiBaseUrl, isApiConfigured, isAuth0Configured } from "../lib/api/config";
import { getAuth0RedirectUri } from "../lib/auth0-redirect";
import { resetLocalSubscriptionsForAuthLogout } from "../lib/db";
import { rememberNavForAfterAuthRedirect } from "../lib/postLoginNav";
import { clearWelcomeContinued } from "../lib/welcome-choice";
import { DesktopDownloadCard } from "./DesktopDownloadCard";
import { requestManualAppUpdateCheck } from "./UpdateChecker";

function formatAccountApiError(e: unknown): string {
  const msg = e instanceof Error ? e.message : String(e);
  const isNetwork =
    msg === "Failed to fetch" ||
    msg.includes("NetworkError") ||
    msg.toLowerCase().includes("load failed");
  if (!isNetwork) return msg;
  const api = getApiBaseUrl() || "(unset)";
  let isHostedApi = false;
  if (api !== "(unset)") {
    try {
      const host = new URL(api).hostname.toLowerCase();
      isHostedApi = host !== "localhost" && host !== "127.0.0.1";
    } catch {
      isHostedApi = true;
    }
  }
  const lines = [
    "Could not reach the API (" + msg + ").",
    "",
    "• VITE_API_URL: " + api,
  ];
  if (isHostedApi) {
    lines.push(
      "• Hosted API: allow this app’s origin in the API’s CORS list (localhost vs 127.0.0.1 are different), or set CORS_ALLOW_LOCAL_DEV=true on the server.",
      "• Auth0: Allowed Web Origins / Callback URLs / Logout URLs must match this app’s origin (web: http://localhost:1420 or https://your-site; desktop: often http://tauri.localhost or https://tauri.localhost — check the address bar or set VITE_AUTH0_REDIRECT_URI).",
    );
  } else {
    lines.push(
      "• Local API: cd services/api && npm run dev (port 8787 by default).",
      "• CORS: in services/api/.env, CORS_ORIGINS must include this app’s exact origin (http://localhost:1420 and http://127.0.0.1:1420 are different).",
    );
  }
  return lines.join("\n");
}

export function AccountPage() {
  return (
    <div className="space-y-6">
      <DesktopDownloadCard />
      {isTauri() && (
        <div className="rounded-xl border border-border bg-surface-1/80 p-5 text-sm backdrop-blur-sm">
          <p className="font-display font-semibold text-text">App updates</p>
          <p className="mt-1 text-text-muted">
            Dripwatch checks for updates when you open the desktop app. You&apos;ll see a bar at the
            top when a new version is downloading or ready to install.
          </p>
          <button
            type="button"
            onClick={() => requestManualAppUpdateCheck()}
            className="mt-3 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-text hover:bg-surface-2"
          >
            Check for updates now
          </button>
        </div>
      )}
      {!isApiConfigured() ? (
        <div className="rounded-xl border border-border bg-surface-1 p-6 text-sm text-text-muted">
          <p className="font-medium text-text">Cloud account</p>
          <p className="mt-2">
            Set <code className="text-accent">VITE_API_URL</code> to your API base URL to enable
            sign-in and cloud sync.
          </p>
        </div>
      ) : !isAuth0Configured() ? (
        <div className="rounded-xl border border-border bg-surface-1 p-6 text-sm text-text-muted">
          <p className="font-medium text-text">Authentication</p>
          <p className="mt-2">
            Configure <code className="text-accent">VITE_AUTH0_DOMAIN</code> and{" "}
            <code className="text-accent">VITE_AUTH0_CLIENT_ID</code> (and optional API audience) to
            enable Google and username/password via Auth0.
          </p>
        </div>
      ) : (
        <AccountPageAuthed />
      )}
    </div>
  );
}

function AccountPageAuthed() {
  const auth0 = useAuth0();
  const [me, setMe] = useState<MeResponse | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setError(null);
      if (auth0.isAuthenticated) {
        try {
          await exchangeSessionFromAuth0({
            getAccessTokenSilently: auth0.getAccessTokenSilently,
            getIdTokenClaims: auth0.getIdTokenClaims,
          });
        } catch (ex) {
          console.warn("Session exchange on account refresh:", ex);
        }
      }
      setMe(await fetchMe());
    } catch (e) {
      setError(formatAccountApiError(e));
    }
  }, [
    auth0.isAuthenticated,
    auth0.getAccessTokenSilently,
    auth0.getIdTokenClaims,
  ]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("account") === "success" || params.get("account") === "cancel") {
      void refresh();
    }
  }, [refresh]);

  async function handleLogout() {
    setError(null);
    clearWelcomeContinued();
    resetLocalSubscriptionsForAuthLogout();
    try {
      await logoutApiSession();
    } catch (e) {
      console.warn(e);
    }
    await auth0.logout({ logoutParams: { returnTo: getAuth0RedirectUri() } });
  }

  return (
    <div className="space-y-6">
      {error && (
        <div
          className="rounded-xl border border-danger/40 bg-surface-1 px-4 py-3 text-sm text-danger"
          role="alert"
        >
          <pre className="whitespace-pre-wrap font-sans">{error}</pre>
        </div>
      )}

      {!auth0.isAuthenticated ? (
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => {
              rememberNavForAfterAuthRedirect("account");
              void auth0.loginWithRedirect({
                authorizationParams: { redirect_uri: getAuth0RedirectUri() },
              });
            }}
            className="rounded-xl bg-accent px-4 py-2.5 text-sm font-semibold text-surface-0 shadow-lg shadow-accent/25 hover:brightness-110"
          >
            Log in
          </button>
          <button
            type="button"
            onClick={() => {
              rememberNavForAfterAuthRedirect("account");
              void auth0.loginWithRedirect({
                authorizationParams: {
                  redirect_uri: getAuth0RedirectUri(),
                  connection: "google-oauth2",
                },
              });
            }}
            className="rounded-xl border border-border bg-surface-1 px-4 py-2.5 text-sm font-semibold text-text hover:bg-surface-2"
          >
            Continue with Google
          </button>
        </div>
      ) : (
        <div className="space-y-4 rounded-xl border border-border bg-surface-1 p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-text-muted">Signed in</p>
              <p className="font-medium text-text">{auth0.user?.email ?? "—"}</p>
            </div>
            <button
              type="button"
              onClick={() => void handleLogout()}
              className="rounded-lg border border-border px-3 py-1.5 text-sm text-text-muted hover:bg-surface-2"
            >
              Log out
            </button>
          </div>

          <div className="border-t border-border pt-4">
            <p className="text-xs uppercase tracking-wide text-text-muted">Cloud sync</p>
            <p className="mt-1 text-sm text-text-muted">
              {me === undefined
                ? "Loading…"
                : me
                  ? `Linked as ${me.email}.`
                  : "Not linked — wait a moment."}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
